import type { MouseEvent, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Paper, Popover } from '@mui/material';

import { useFilterContext } from 'src/components/app/context/FilterContext';
import { useOpenPopup } from 'src/components/app/context/OpenPopupContext';
import type { OpenPopupKeyValue } from 'src/components/followUp/common/types';

import AllCheckbox from './AllCheckbox';
import ApplyFilterButton from './ApplyFilterButton';
import FilterListItem from './FilterListItem';
import OpenFilterGroupButton from './OpenFilterGroupButton';

import { styles } from './index.styles';

const isSameArray = (arr1: Array<string>, arr2: Array<string>): boolean => {
  if (arr1.length !== arr2.length) {
    return false;
  }
  const sortedArr1 = arr1.slice().sort();
  const sortedArr2 = arr2.slice().sort();

  return sortedArr1.every((element, index) => element === sortedArr2[index]);
};

// empty default array as reference for hooks to prevent rerendering
const DEFAULT_FILTERS: never[] = [];

const FilterSection = <FilterOptionType extends string>({
  filterKey,
  allFilterOptions,
  children,
  searchBar,
  popupKey,
}: {
  filterKey: string;
  allFilterOptions: Array<{ value: FilterOptionType; displayString: string }>;
  children: ReactNode;
  searchBar?: ReactNode;
  popupKey: OpenPopupKeyValue;
}) => {
  const { openPopupKey, setOpenPopupKey } = useOpenPopup();
  const isOpen = Boolean(openPopupKey && popupKey && openPopupKey === popupKey);
  const { setFilter, filterGroups } = useFilterContext();

  const filterGroupForKey = filterGroups[filterKey];
  // Single shared instance for the AllCheckbox and every FilterListItem;
  // building it per item allocates O(n²) objects per render.
  const allFilterValues = useMemo(
    () =>
      allFilterOptions.map((option) => ({
        label: option.displayString,
        filterExpr: { value: option.value },
      })),
    [allFilterOptions],
  );
  const appliedFilters = useMemo(
    () => filterGroupForKey ?? DEFAULT_FILTERS,
    [filterGroupForKey],
  ) as Array<{
    label: string;
    filterExpr: { value: FilterOptionType };
  }>;

  const [selectedFilters, setSelectedFilters] =
    useState<Array<{ label: string; filterExpr: { value: FilterOptionType } }>>(
      appliedFilters,
    );

  useEffect(() => {
    setSelectedFilters(appliedFilters);
  }, [appliedFilters]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const handleClick = (event: MouseEvent<HTMLElement>) => {
    if (anchorEl) {
      setSelectedFilters(appliedFilters);
      setAnchorEl(null);
    } else {
      setOpenPopupKey(popupKey);
      setAnchorEl(event.currentTarget);
    }
  };
  const doClose = useCallback(() => {
    setAnchorEl(null);
    setOpenPopupKey(null);
  }, [setAnchorEl, setOpenPopupKey]);

  useEffect(() => {
    if (!isOpen && anchorEl) {
      setAnchorEl(null);
    }
  }, [isOpen, anchorEl]);

  const isDropdownOpen = Boolean(anchorEl);
  const dropdownId = isDropdownOpen ? 'simple-popper' : undefined;
  return (
    <Box>
      <OpenFilterGroupButton
        filter={appliedFilters?.map((appliedFilter) => appliedFilter.label)}
        onClick={handleClick}
        showTooltip={!anchorEl}
      >
        {children}
      </OpenFilterGroupButton>

      <Popover
        id={dropdownId}
        open={isDropdownOpen}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        onClose={() => doClose()}
        sx={styles.filterSectionPopover}
      >
        <Paper
          sx={[styles.filterSectionDropdown, { width: anchorEl?.clientWidth }]}
        >
          {searchBar}
          <AllCheckbox
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            allFilterValues={allFilterValues}
          />
          {allFilterValues.map((filterItem) => (
            <FilterListItem
              key={filterItem.filterExpr.value + filterItem.label}
              filterItem={filterItem}
              selectedFilters={selectedFilters}
              setSelectedFilters={setSelectedFilters}
              allFilterValues={allFilterValues}
            />
          ))}
          <ApplyFilterButton
            disabled={isSameArray(
              selectedFilters.map(({ label }) => label),
              appliedFilters.map(({ label }) => label),
            )}
            onClick={() => {
              setFilter(filterKey, selectedFilters);
              doClose();
            }}
          />
        </Paper>
      </Popover>
    </Box>
  );
};

export default FilterSection;
